import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Overline, Tag, Disclaimer, EmptyState } from "../components/shared";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import { Rocket, Target, Lightbulb, Layers, Loader2, ChevronRight, Check, ArrowRight, Sparkles } from "lucide-react";

const STEPS = [
  { n: 1, label: "Opportunity Discovery", icon: Target },
  { n: 2, label: "Concept Generator", icon: Lightbulb },
  { n: 3, label: "Initial Architecture", icon: Layers },
];

function Meta({ label, value, tone }) {
  return (
    <div className="py-1.5 border-b border-border/40 last:border-0">
      <span className="overline text-muted-foreground text-[9px]">{label}</span>
      <div className={cn("text-xs mt-0.5", tone || "text-foreground")}>{value}</div>
    </div>
  );
}

export default function DesignMyICO() {
  const [step, setStep] = useState(1);
  const [opps, setOpps] = useState([]);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [customOpp, setCustomOpp] = useState("");
  const [concepts, setConcepts] = useState(null);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [arch, setArch] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/opportunities").then((r) => setOpps(r.data.opportunities)); }, []);

  const genConcepts = async () => {
    setLoading(true); setConcepts(null); setSelectedConcept(null); setArch(null);
    try {
      const body = selectedOpp ? { opportunity_id: selectedOpp.id } : { opportunity_name: customOpp };
      const { data } = await api.post("/design/concepts", body);
      setConcepts(data.concepts);
      if (!data.llm_ok) toast.warning("Showing template concepts (AI generator unavailable).");
      setStep(2);
    } catch { toast.error("Could not generate concepts"); }
    finally { setLoading(false); }
  };

  const genArch = async (concept) => {
    setSelectedConcept(concept); setLoading(true); setArch(null); setStep(3);
    try {
      const { data } = await api.post("/design/architecture", { concept });
      setArch(data.architecture);
      if (!data.llm_ok) toast.warning("Showing template architecture (AI generator unavailable).");
    } catch { toast.error("Could not generate architecture"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-5 fade-up" data-testid="design-page">
      <div><Overline>Ideation Workflow</Overline><h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1">Design My ICO</h1>
        <p className="text-sm text-muted-foreground mt-1">Move from market opportunity → concept → initial architecture. Full tokenomics come later, once you freeze a concept.</p></div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.n}>
            <button onClick={() => s.n < step && setStep(s.n)} className={cn("flex items-center gap-2 px-3 py-2 rounded-sm border text-sm font-medium transition-colors", step === s.n ? "bg-primary text-primary-foreground border-primary" : step > s.n ? "bg-accent border-border text-foreground" : "bg-card border-border text-muted-foreground")}>
              <span className={cn("w-5 h-5 rounded-full flex items-center justify-center text-[11px] num", step > s.n ? "bg-emerald-500 text-white" : step === s.n ? "bg-white/20" : "bg-muted")}>{step > s.n ? <Check className="w-3 h-3" /> : s.n}</span>
              {s.label}
            </button>
            {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1 */}
      {step === 1 && (
        <div className="space-y-4">
          <Disclaimer>Opportunities are surfaced from the tracked market. Pick one, or describe your own focus area.</Disclaimer>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {opps.map((o) => (
              <button key={o.id} onClick={() => { setSelectedOpp(o); setCustomOpp(""); }} data-testid={`design-opp-${o.id}`}
                className={cn("text-left bg-card border p-4 transition-colors", selectedOpp?.id === o.id ? "border-primary ring-1 ring-primary" : "border-border/60 hover:border-primary/40")}>
                <div className="flex items-center justify-between"><span className="font-heading font-bold text-sm">{o.name}</span><Tag>{o.trend}</Tag></div>
                <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{o.problem}</p>
                <div className="text-[11px] text-muted-foreground mt-2">Confidence {o.confidence}% · {o.competitor_count} competitors</div>
              </button>
            ))}
          </div>
          <div className="bg-card border border-border/60 p-4">
            <div className="overline text-muted-foreground mb-1">Or describe your own opportunity</div>
            <input value={customOpp} onChange={(e) => { setCustomOpp(e.target.value); setSelectedOpp(null); }} placeholder="e.g. On-chain reputation for freelance marketplaces"
              data-testid="design-custom-opp" className="w-full h-10 px-3 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-ring" />
          </div>
          <button onClick={genConcepts} disabled={loading || (!selectedOpp && !customOpp.trim())} data-testid="design-generate-concepts"
            className="inline-flex items-center gap-2 h-10 px-5 bg-primary text-primary-foreground text-sm font-semibold rounded-sm hover:bg-primary/90 disabled:opacity-50 transition-colors">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate Concepts <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Step 2 */}
      {step === 2 && (
        <div className="space-y-4">
          {loading || !concepts ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {concepts.map((c, i) => (
                <div key={i} className="bg-card border border-border/60 p-5 flex flex-col" data-testid={`design-concept-${i}`}>
                  <div className="w-8 h-8 bg-accent flex items-center justify-center rounded-sm mb-3"><Lightbulb className="w-4 h-4 text-primary" /></div>
                  <h3 className="font-heading font-bold text-lg tracking-tight">{c.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1.5 flex-1">{c.use_case}</p>
                  <div className="mt-3 space-y-0.5">
                    <Meta label="Target problem" value={c.target_problem} />
                    <Meta label="Users" value={c.target_users} />
                    <Meta label="Revenue" value={c.revenue_model} />
                    <Meta label="Token needed" value={c.token_requirement} />
                    <Meta label="Advantage" value={c.competitive_advantage} />
                    <Meta label="Decentralization" value={c.decentralization_level} />
                    <Meta label="Regulatory" value={c.regulatory_complexity} />
                    <Meta label="Technical" value={c.technical_complexity} />
                  </div>
                  <button onClick={() => genArch(c)} data-testid={`design-select-concept-${i}`}
                    className="mt-4 inline-flex items-center justify-center gap-2 h-9 bg-primary text-primary-foreground text-sm font-semibold rounded-sm hover:bg-primary/90 transition-colors">
                    Select & design architecture <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 3 */}
      {step === 3 && (
        <div className="space-y-4">
          {loading || !arch ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> : (
            <>
              <div className="bg-accent border border-border/60 p-4">
                <div className="overline text-muted-foreground">Selected concept</div>
                <div className="font-heading font-bold text-lg">{selectedConcept?.name}</div>
                <p className="text-sm text-muted-foreground mt-0.5">{selectedConcept?.use_case}</p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-card border border-border/60 p-5">
                  <h3 className="font-heading font-bold text-sm mb-3">Blockchain Options</h3>
                  {(arch.blockchain_options || []).map((b, i) => (
                    <div key={i} className="border-b border-border/40 last:border-0 py-2"><div className="font-semibold text-sm">{b.name}</div><div className="text-xs text-emerald-700 mt-0.5">+ {b.pros}</div><div className="text-xs text-red-600">− {b.cons}</div></div>
                  ))}
                </div>
                <div className="bg-card border border-border/60 p-5">
                  <h3 className="font-heading font-bold text-sm mb-3">Token Model Options</h3>
                  {(arch.token_model_options || []).map((b, i) => (
                    <div key={i} className="border-b border-border/40 last:border-0 py-2"><div className="font-semibold text-sm">{b.name}</div><div className="text-xs text-emerald-700 mt-0.5">+ {b.pros}</div><div className="text-xs text-red-600">− {b.cons}</div></div>
                  ))}
                </div>
                <div className="bg-card border border-border/60 p-5">
                  <h3 className="font-heading font-bold text-sm mb-3">Jurisdiction Options</h3>
                  {(arch.jurisdiction_options || []).map((j, i) => (
                    <div key={i} className="border-b border-border/40 last:border-0 py-2"><div className="font-semibold text-sm">{j.name}</div><div className="text-xs text-muted-foreground mt-0.5">{j.why}</div></div>
                  ))}
                </div>
                <div className="bg-card border border-border/60 p-5 space-y-3">
                  <div><h3 className="font-heading font-bold text-sm mb-1">Decentralization Approach</h3><p className="text-sm text-muted-foreground">{arch.decentralization_approach}</p></div>
                  <div><h3 className="font-heading font-bold text-sm mb-1">Corporate Structure</h3><div className="flex flex-wrap gap-1.5">{(arch.corporate_structure_options || []).map((c) => <Tag key={c}>{c}</Tag>)}</div></div>
                  <div><h3 className="font-heading font-bold text-sm mb-1">Go-to-Market</h3><p className="text-sm text-muted-foreground">{arch.go_to_market}</p></div>
                  <div><h3 className="font-heading font-bold text-sm mb-1">Launch Model</h3><p className="text-sm text-muted-foreground">{arch.launch_model}</p></div>
                </div>
              </div>
              <Disclaimer>Initial architecture only. Detailed tokenomics, smart-contract design, and regulatory structure come after you freeze a concept. Not legal or investment advice.</Disclaimer>
            </>
          )}
        </div>
      )}
    </div>
  );
}
